/**
 * spiceboys: Scroll-Autoplay fuer hochgeladene Videos in einer <deferred-media>.
 *
 * Ziel: das Video startet von allein, sobald man hinscrollt, wird aber nicht
 * schon beim Seitenaufruf geladen. Horizons eigenes Autoplay schreibt das
 * <video autoplay> direkt ins Server-HTML (snippets/video.liquid) - der Browser
 * laedt die Datei dann sofort, egal ob sie je sichtbar wird (gemessen 9,5 MB auf
 * der PDP, docs/speed-baseline-2026-08.md).
 *
 * Ablauf:
 *   1. Server-HTML enthaelt nur Poster + <template> (wie beim Klick-Pfad).
 *   2. Kommt das Video bis auf PRELOAD_MARGIN an den Viewport heran, wird es aus
 *      dem <template> eingesetzt (stumm, ohne autoplay-Attribut). So laedt der
 *      Browser Metadaten und Poster noch ausserhalb des Bildes - der Poster-
 *      Wechsel ("weisser Blitz", siehe CLAUDE.md) passiert unsichtbar.
 *   3. Ab PLAY_RATIO Sichtbarkeit: play(). Ganz aus dem Bild: pause().
 *      Tab im Hintergrund: pause(), zurueck: weiter.
 *   4. Pausiert der Nutzer selbst (Tap-Overlay) oder ist das Video zu Ende
 *      (loop aus), startet es nicht erneut von allein.
 *   5. prefers-reduced-motion, Datensparmodus oder 2G: nichts passiert - Poster
 *      bleibt, Klick auf Play startet wie bisher mit Ton.
 *
 * Bewusst NICHT ueber Horizons DeferredMedia.loadContent(): das dispatcht
 * MediaStartedPlayingEvent und pausiert damit alle anderen Videos der Seite -
 * schon im Vorlade-Abstand, also evtl. ein Video, das gerade sichtbar laeuft.
 * Die drei Schritte daraus (Attribut, Klon anhaengen, Poster-Klasse) sind hier
 * nachgebaut. Aendert Horizon loadContent(), hier mitziehen
 * (docs/theme-update-migration.md).
 *
 * Referenzen auf das <video> werden nie gecacht (Getter), gleiche Regel wie in
 * spiceboys-video-controls.js und beim Family-Pack.
 */

const TAG_NAME = 'spiceboys-video-autoplay';

/** Vorlade-Abstand: so frueh wird das <video> eingesetzt. */
const PRELOAD_MARGIN = '400px 0px';

/** Anteil, der sichtbar sein muss, bevor das Video startet. */
const PLAY_RATIO = 0.25;

class SpiceboysVideoAutoplay extends HTMLElement {
  /** @type {HTMLElement | null} */
  #media = null;

  /** @type {IntersectionObserver | null} */
  #nearObserver = null;

  /** @type {IntersectionObserver | null} */
  #viewObserver = null;

  /** @type {HTMLVideoElement | null} */
  #boundVideo = null;

  #inView = false;

  /** Nutzer hat selbst pausiert -> nicht von allein weiterspielen. */
  #userPaused = false;

  /** Die naechste 'pause' kommt von uns (Scroll/Tab), nicht vom Nutzer. */
  #autoPaused = false;

  /**
   * Video ist einmal durchgelaufen (loop aus) -> nicht von allein neu starten.
   * Eigenes Flag statt video.ended: WebKit setzt ended/currentTime nach dem
   * Weg- und Zurueckscrollen teils zurueck (gemessen: 10 s -> 9 s, ended false).
   */
  #finished = false;

  connectedCallback() {
    this.#media = this.closest('deferred-media');
    if (!this.#media || !this.#autoplayAllowed()) return;
    if (!this.#templateVideo() && !this.video) return;

    if (!this.#media.hasAttribute('data-media-loaded')) {
      this.#media.setAttribute('data-sb-autoplay-pending', '');
    }

    this.#nearObserver = new IntersectionObserver(this.#onNear, { rootMargin: PRELOAD_MARGIN });
    this.#viewObserver = new IntersectionObserver(this.#onView, { threshold: [0, PLAY_RATIO] });
    this.#nearObserver.observe(this.#media);
    this.#viewObserver.observe(this.#media);

    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#nearObserver?.disconnect();
    this.#viewObserver?.disconnect();
    this.#nearObserver = null;
    this.#viewObserver = null;
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#unbindVideo();
  }

  /** @returns {HTMLVideoElement | null} */
  get video() {
    return this.#media?.querySelector(':scope > video') ?? null;
  }

  /** Bewegung und Datenvolumen respektieren. */
  #autoplayAllowed() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    // Network Information API gibt es nur in Chromium - fehlt sie, gilt: erlaubt.
    const connection = /** @type {any} */ (navigator).connection;
    if (connection?.saveData) return false;
    if (typeof connection?.effectiveType === 'string' && connection.effectiveType.endsWith('2g')) return false;

    return true;
  }

  /** @returns {HTMLVideoElement | null} */
  #templateVideo() {
    const template = this.#media?.querySelector(':scope > template');
    const element = template instanceof HTMLTemplateElement ? template.content.firstElementChild : null;
    // Externe Player (YouTube/Vimeo) liegen als <iframe> im Template - die lassen wir in Ruhe.
    return element?.localName === 'video' ? /** @type {HTMLVideoElement} */ (element) : null;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onNear = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#nearObserver?.disconnect();
    this.#nearObserver = null;
    this.#insertVideo();
  };

  /** @param {IntersectionObserverEntry[]} entries */
  #onView = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    this.#inView = entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO;

    if (this.#inView) {
      // Beide Observer feuern im selben Frame, die Reihenfolge ist offen -
      // #insertVideo() ist deshalb idempotent und spielt selbst ab.
      this.#insertVideo();
    } else if (!entry.isIntersecting) {
      this.#pauseBySelf();
    }
  };

  #onVisibility = () => {
    if (document.hidden) {
      this.#pauseBySelf();
    } else if (this.#inView) {
      this.#play();
    }
  };

  #insertVideo() {
    const media = this.#media;
    if (!media) return;

    // Hat der Nutzer schon auf den Poster geklickt, hat Horizon das Video
    // eingesetzt (mit Ton) - dann nur noch Sichtbarkeit steuern. Ein <video>,
    // das schon direkt drinsteht, nie doppeln.
    if (!media.hasAttribute('data-media-loaded') && !this.video) {
      const source = this.#templateVideo();
      if (!source) return;

      const video = /** @type {HTMLVideoElement} */ (source.cloneNode(true));
      // video_tag setzt autoplay immer (Safari-Workaround in media.js). Wir
      // starten selbst - sonst liefe das Video schon im Vorlade-Abstand los.
      video.removeAttribute('autoplay');
      // Autoplay ist nur stumm erlaubt. Attribut + Property: Safari liest beim
      // Einfuegen das Attribut, Chrome beim play() die Property.
      video.setAttribute('muted', '');
      video.muted = true;
      video.playsInline = true;

      media.setAttribute('data-media-loaded', 'true');
      media.appendChild(video);
      media.querySelector(':scope > [ref="deferredMediaPlayButton"]')?.classList.add('deferred-media__playing');
    }

    this.#bindVideo();
    if (this.#inView) this.#play();
  }

  #play() {
    const video = this.video;
    if (!video || this.#userPaused || this.#finished || video.ended || !video.paused) return;

    video.play().then(this.#clearPending, (error) => {
      // AbortError: wir haben selbst pausiert (weggescrollt, bevor es lief).
      // Nichts tun - beim naechsten Eintritt startet es regulaer.
      if (error?.name === 'AbortError') return;

      // Ton ist an (Nutzer hat entstummt) und der Browser verlangt dafuer eine
      // frische Geste: stumm weiterspielen - aber nur, wenn es noch sichtbar ist.
      if (error?.name === 'NotAllowedError' && !video.muted && this.#inView && !document.hidden) {
        video.muted = true;
        video.play().then(this.#clearPending, this.#clearPending);
        return;
      }

      // Sonst z. B. iOS-Stromsparmodus: Autoplay ist gesperrt. Ohne pending
      // zeigt das Overlay das Play-Icon, Tippen startet das Video.
      this.#clearPending();
    });
  }

  #clearPending = () => {
    this.#media?.removeAttribute('data-sb-autoplay-pending');
  };

  #pauseBySelf() {
    const video = this.video;
    if (!video || video.paused) return;
    this.#autoPaused = true;
    video.pause();
  }

  #bindVideo() {
    const video = this.video;
    if (!video || this.#boundVideo === video) return;
    this.#unbindVideo();
    this.#boundVideo = video;
    video.addEventListener('play', this.#onPlay);
    video.addEventListener('pause', this.#onPause);
    video.addEventListener('ended', this.#onEnded);
  }

  #unbindVideo() {
    if (!this.#boundVideo) return;
    this.#boundVideo.removeEventListener('play', this.#onPlay);
    this.#boundVideo.removeEventListener('pause', this.#onPause);
    this.#boundVideo.removeEventListener('ended', this.#onEnded);
    this.#boundVideo = null;
  }

  #onPlay = () => {
    // Auch nach dem Ende: tippt der Nutzer auf Play, laeuft es wieder normal.
    this.#userPaused = false;
    this.#autoPaused = false;
    this.#finished = false;
    this.#clearPending();
  };

  #onEnded = () => {
    this.#finished = true;
  };

  #onPause = () => {
    // 'pause' feuert asynchron - deshalb Flag statt Try/Finally um video.pause().
    if (this.#autoPaused) return;

    // Pausiert der Browser selbst, waehrend das Video nicht (mehr) im Bild ist
    // (Stromsparen offscreen, Tab im Hintergrund), ist das keine Nutzer-
    // entscheidung - sonst liefe es beim Zurueckscrollen nie wieder an.
    if (!this.#inView || document.hidden) return;

    // Alles andere im sichtbaren Zustand (Tap-Overlay, Ende ohne loop, Horizon
    // pausiert wegen eines anderen, angeklickten Videos) ist eine bewusste Pause.
    this.#userPaused = true;
  };
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, SpiceboysVideoAutoplay);
}
